import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { GripVertical } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listAllMenuItemsByCategory, reorderMenuItems } from "@/lib/api/admin";
import type { MenuCategory, MenuItem } from "@/lib/api/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { formatCents } from "@/lib/utils/number";

type ReorderItemsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  categories: MenuCategory[];
};

export function ReorderItemsDialog({
  open,
  onOpenChange,
  storeId,
  categories,
}: ReorderItemsDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [categoryId, setCategoryId] = useState("");
  const [ordered, setOrdered] = useState<MenuItem[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["menu-items-reorder", storeId, categoryId],
    queryFn: () => listAllMenuItemsByCategory(user!, storeId, categoryId),
    enabled: open && !!categoryId,
  });

  // Sync the local order when the fetched item set changes (category switch,
  // refetch), but not on every render so an in-flight drag isn't shuffled.
  const itemsKey = items
    .map((i) => i.id)
    .sort()
    .join(",");
  const [prevItemsKey, setPrevItemsKey] = useState("");
  if (itemsKey !== prevItemsKey) {
    setPrevItemsKey(itemsKey);
    setOrdered(items);
  }

  const reorderMutation = useMutation({
    mutationFn: async (ids: string[]) => reorderMenuItems(user!, storeId, categoryId, ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["menu-items-reorder", storeId] });
      void qc.invalidateQueries({ queryKey: ["menu-items", storeId] });
    },
    onError: () => {
      setOrdered(items);
      toast.error("Failed to reorder items.");
    },
  });

  function handleDragStart(index: number) {
    setDragIndex(index);
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    setOverIndex(index);
  }

  function handleDrop(e: React.DragEvent, index: number) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...ordered];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setOrdered(next);
    setDragIndex(null);
    setOverIndex(null);
    reorderMutation.mutate(next.map((i) => i.id));
  }

  function handleDragEnd() {
    setDragIndex(null);
    setOverIndex(null);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reorder Items</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">Select a category…</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {!categoryId ? (
            <p className="text-sm text-muted-foreground">Pick a category to reorder its items.</p>
          ) : isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="size-5 border-primary" />
            </div>
          ) : ordered.length === 0 ? (
            <EmptyState title="No items" description="This category has no items." />
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Drag to reorder. Items appear on the menu in this order.
              </p>
              <ul className="divide-y rounded-md border">
                {ordered.map((item, i) => (
                  <li
                    key={item.id}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDrop={(e) => handleDrop(e, i)}
                    onDragEnd={handleDragEnd}
                    className={`transition-colors ${overIndex === i ? "bg-primary/5" : ""}`}
                  >
                    <div className="flex items-center gap-2 p-3">
                      <GripVertical className="size-4 text-muted-foreground cursor-grab shrink-0" />
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="size-8 rounded object-cover shrink-0"
                        />
                      )}
                      <span className="flex-1 text-sm font-medium truncate">{item.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatCents(item.price_cents)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
